'use client';

import { useEffect, useState } from 'react';
import { _getCardPaymentAcceptPoint } from '~/services/actions/CSU/get-card-payment-accept-point';
import { cardPaymentSettingsPayload } from '~/utils/payment/card-payment-accept-settings';
import { IPaymentConnector } from '~/interfaces/payment';
import PaymentContainer from '~/modules/payment/components/payment-container';
import createVirtualCart from '~/utils/create-virtual-cart';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { createCart } from '~/services/actions/CSU/create-cart';
import {
  CheckoutPaymentInstrument,
  Maybe,
  MfrmDeliveryService,
  SynchronyConfiguration,
  ProgressiveConfiguration,
  AffirmConfiguration,
  UsAePayErrorMessage,
  FeatureFlag,
} from '~/gql/__generated__/types';
import {
  usePaymentReview,
  useSelectedAmountOption,
  useSetSelectedPaymentOption,
  wmapStore,
} from '~/store/wmap-store';
import Styles from '../payment/components/credit-card.module.scss';
import CustomSkeleton from '~/components/custom-skeleton';
import { getFinancingOfferDetails } from '~/modules/payment/synchrony/utils/get-financing-offer-details';
import { IFinancingOfferFormatted } from '~/interfaces/synchrony/synchrony-interfaces';
import {
  MINIMUM_FINANCING_AMOUNT,
  SYNCHRONY_DETAILS,
  TYPES,
  WMAP_PREFIX,
} from '~/modules/payment/constants';

import {
  INPUT_ID_OTHER_AMOUNT,
  INPUT_ID_OUTSTANDING_AMOUNT,
} from '~/constant/constants';
import { sendSynchronyExistingAccountUtag } from '~/utils/tealium';
import { mockPaymentConnectors } from '~/mocks/mockPaymentContainer';
import { useSetSyfOverlay } from '~/store/wmap-store';

export interface WmapPaymentProps {
  orderDetails: ISalesOrderResponse;
  paymentInstrumentList: Array<Maybe<CheckoutPaymentInstrument>>;
  mfrmDeliveryService: MfrmDeliveryService;
  isForterValidationEnabled: boolean;
  synchronyConfiguration: SynchronyConfiguration;
  progressiveConfiguration: ProgressiveConfiguration;
  affirmConfiguration: AffirmConfiguration;
  financingExcludedStores?: Maybe<string>;
  usaEPayErrorMessages?: Maybe<Maybe<UsAePayErrorMessage>[]> | undefined;
  featureFlags?: Maybe<Maybe<FeatureFlag>[]> | undefined;
  adyenPilotStores?: Maybe<string>;
  isForterValidationEnabledForAdyen?: boolean;
}

const WmapPayment: React.FC<WmapPaymentProps> = ({
  orderDetails,
  paymentInstrumentList,
  mfrmDeliveryService,
  isForterValidationEnabled,
  synchronyConfiguration,
  progressiveConfiguration,
  financingExcludedStores,
  affirmConfiguration,
  usaEPayErrorMessages,
  featureFlags,
  adyenPilotStores,
  isForterValidationEnabledForAdyen,
}) => {
  const setSyfOverlay = useSetSyfOverlay();
  const setSelectedPayment = useSetSelectedPaymentOption();
  orderDetails.balanceAmount = orderDetails.AmountDue;
  const [paymentConnectors, setPaymentConnectors] = useState<
    IPaymentConnector[]
  >([]);
  const [cartId, setCartId] = useState<string | null>(null);

  const activeOption = useSelectedAmountOption();

  // Filtering logic for Progressive and Synchrony
  const progressivePilotStores = progressiveConfiguration?.progressive
    ?.progressivePilotStores
    ? progressiveConfiguration.progressive.progressivePilotStores
        .split(',')
        .map((s) => s.trim())
    : [];

  const synchronyPilotStores = synchronyConfiguration?.financingConfigurations
    ?.synchronyPilotStores
    ? synchronyConfiguration.financingConfigurations.synchronyPilotStores
        .split(',')
        .map((s) => s.trim())
    : [];
  const affirmPilotStores = affirmConfiguration.affirmPilotStores
    ? affirmConfiguration.affirmPilotStores
        .split(',')
        .map((s: string) => s.trim())
    : [];

  const adyenPilotStoresList = adyenPilotStores
    ? adyenPilotStores.split(',').map((s: string) => s.trim())
    : [];
  const excludedStores = financingExcludedStores
    ? financingExcludedStores.split(',').map((s) => s.trim())
    : [];

  const currentStoreId = orderDetails?.StoreId;

  // Check if Adyen exists in payment instrument list
  const hasAdyenInInstrumentList = paymentInstrumentList.some(
    (c) =>
      c?.paymentTenderType?.split(';')[0].toLowerCase() ===
      TYPES.ADYEN.toLowerCase(),
  );

  // Determine if Adyen should be blocked
  const calculateAdyenBlocked = (): boolean => {
    // If Adyen doesn't exist in instrument list, block it (show USAePay)
    if (!hasAdyenInInstrumentList) {
      return true;
    }

    // Adyen exists in instrument list
    // If pilot store list is empty, show Adyen (no restrictions)
    if (adyenPilotStoresList.length === 0) {
      return false;
    }

    // Pilot store list is not empty
    // Check if current store is in the pilot list
    const storeInPilotList = adyenPilotStoresList.includes(currentStoreId);

    if (storeInPilotList) {
      return false; // Show Adyen
    } else {
      return true; // Show USAePay
    }
  };

  const paymentState = {
    isBelowMinimumAmount: orderDetails.AmountDue < MINIMUM_FINANCING_AMOUNT,
    isOtherAmountSelected: activeOption === INPUT_ID_OTHER_AMOUNT,
    isBalanceSelected: activeOption === INPUT_ID_OUTSTANDING_AMOUNT,
    hasPartialPayment: orderDetails.AmountPaid > 0,
    isStoreExcludedFromFinancing:
      excludedStores.length > 0 && excludedStores.includes(currentStoreId),
    isStoreBlockingSynchrony:
      (synchronyPilotStores.length > 0 &&
        !synchronyPilotStores.includes(currentStoreId)) ||
      !paymentInstrumentList.some(
        (c) =>
          c?.paymentTenderType?.toLowerCase() === TYPES.SYNCHRONY.toLowerCase(),
      ),
    isStoreBlockingProgressive:
      (progressivePilotStores.length > 0 &&
        !progressivePilotStores.includes(currentStoreId)) ||
      !paymentInstrumentList.some(
        (c) =>
          c?.paymentTenderType?.toLowerCase() ===
          TYPES.PROGRESIVE.toLowerCase(),
      ),
    isStoreBlockingAffirm:
      (affirmPilotStores.length > 0 &&
        !affirmPilotStores.includes(currentStoreId)) ||
      !paymentInstrumentList.some(
        (c) =>
          c?.paymentTenderType?.toLowerCase() === TYPES.AFFIRM.toLowerCase(),
      ),
    isStoreBlockedAdyen: calculateAdyenBlocked(),
  };
  const skeletonCountBlocking = (state: {
    isStoreBlockingAffirm?: boolean;
    isStoreBlockingProgressive?: boolean;
    isStoreBlockingSynchrony?: boolean;
  }) => {
    return [
      !!state.isStoreBlockingAffirm,
      !!state.isStoreBlockingProgressive,
      !!state.isStoreBlockingSynchrony,
    ].filter(Boolean).length;
  };
  const skeletonBlockedCount = skeletonCountBlocking(paymentState);
  useEffect(() => {
    handleSyfModalHtml();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userSelectedAmount = wmapStore.getState().userSelectedAmount;
        const createCartResponse = await createCart();
        const generatedCartId = createCartResponse.Id;

        if (!generatedCartId) {
          console.error('Cart ID not generated');
          return;
        }

        setCartId(generatedCartId);
        await createVirtualCart(
          orderDetails,
          generatedCartId,
          mfrmDeliveryService,
        );

        if (!paymentInstrumentList?.length) return;

        const filteredPaymentInstruments = (paymentInstrumentList || []).filter(
          (pi) => {
            const tenderType = pi?.paymentTenderType?.split(';')[0];
            if (!tenderType) return false;

            // Exclude both Synchrony and Progressive for excluded stores
            // Allow only credit card payment types (Visa or Adyen)
            if (
              paymentState.isStoreExcludedFromFinancing &&
              tenderType !== TYPES.CREDIT_CARD &&
              tenderType !== TYPES.ADYEN
            ) {
              return false;
            }

            switch (tenderType) {
              case TYPES.PROGRESIVE:
                return (
                  !progressivePilotStores.length ||
                  progressivePilotStores.includes(currentStoreId)
                );
              case TYPES.SYNCHRONY:
                return (
                  !synchronyPilotStores.length ||
                  synchronyPilotStores.includes(currentStoreId)
                );
              case TYPES.AFFIRM:
                return (
                  !affirmPilotStores.length ||
                  affirmPilotStores.includes(currentStoreId)
                );
              case TYPES.CREDIT_CARD:
                return paymentState.isStoreBlockedAdyen;
              case TYPES.ADYEN:
                return !paymentState.isStoreBlockedAdyen;
              default:
                return true;
            }
          },
        );

        const selectedOffer = getFinancingOfferDetails(
          synchronyConfiguration,
          orderDetails.balanceAmount,
        );

        const connectors: (IPaymentConnector | null)[] = await Promise.all(
          filteredPaymentInstruments.map(async (type) => {
            const payload = await cardPaymentSettingsPayload(
              type?.paymentTenderType || '',
              userSelectedAmount,
              extractPlanID(selectedOffer, type),
            );

            if (
              type?.paymentTenderType === TYPES.SYNCHRONY &&
              paymentState.isBelowMinimumAmount
            ) {
              return (
                mockPaymentConnectors.find(
                  (c) => c.tenderType === TYPES.SYNCHRONY,
                ) || null
              );
            }
            try {
              const connectorHtml = await _getCardPaymentAcceptPoint(
                generatedCartId,
                payload,
              );

              return {
                connectorHtml,
                tenderType: type?.paymentTenderType?.split(';')[0] || '',
                config: {
                  heading: type?.heading?.text || '',
                  subHeading: type?.subHeading?.text || '',
                  synchronyButtonText: type?.synchronyButtonText || '',
                  applyNowLink: type?.applyNowLink || '',
                },
              } as IPaymentConnector;
            } catch (error) {
              console.error(
                `[PaymentConnector] Error fetching payment settings for ${type?.paymentTenderType}`,
                error,
              );
              return null;
            }
          }),
        );

        const validConnectors = connectors.filter(
          (connector): connector is IPaymentConnector => connector !== null,
        );

        setPaymentConnectors(validConnectors);
        setSyfOverlay(false);
      } catch (error) {
        console.error(
          `[CheckoutPayment] Error fetching payment settings`,
          error,
        );
      }
    };

    fetchData();
  }, [
    orderDetails,
    paymentInstrumentList,
    mfrmDeliveryService,
    paymentState.isStoreBlockingProgressive,
    paymentState.isStoreBlockingSynchrony,
    paymentState.isStoreExcludedFromFinancing,
  ]);

  // Set default payment option to first connector (credit card)
  // only when nothing is selected or the current selection is no longer available
  useEffect(() => {
    if (paymentConnectors.length === 0 || !paymentConnectors[0]?.tenderType) {
      return;
    }
    const current = wmapStore.getState().selectedPaymentOption;
    const stillValid = paymentConnectors.some(
      (connector) => connector.tenderType === current,
    );
    if (!stillValid) {
      setSelectedPayment(paymentConnectors[0].tenderType);
    }
  }, [paymentConnectors, setSelectedPayment]);

  //do not render this component for payment review page
  const paymentReview = usePaymentReview();
  if (paymentReview.review) {
    return <></>;
  }
  if (!cartId) {
    return (
      <>
        <h4 tabIndex={0}>Payment Type</h4>
        <div
          className={`${Styles.paymentContainer}
           ${Styles.fixedHeight}
                
            ${Styles.count}${skeletonBlockedCount}
             ${
               (paymentState.hasPartialPayment ||
                 (paymentState.isStoreBlockingProgressive &&
                   paymentState.isStoreBlockingSynchrony &&
                   paymentState.isStoreBlockingAffirm) ||
                 paymentState.isStoreExcludedFromFinancing) &&
               Styles.ccOnly
             }`}
        >
          <div className={Styles.loader}>
            <div className={Styles.skeletonWrapper}>
              <div className={Styles.radioSkeleton}>
                <CustomSkeleton type="text" height="56" width="100%" />
              </div>
              <div>
                <CustomSkeleton type="text" height="60px" width="100%" />
              </div>
              <div className={Styles.width50}>
                <CustomSkeleton type="text" height="60" />
                <CustomSkeleton type="text" height="60" />
              </div>
              {!paymentState.isStoreBlockingSynchrony &&
                !paymentState.isStoreExcludedFromFinancing && (
                  <div
                    className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
                  >
                    <CustomSkeleton type="text" height="56" width="100%" />
                  </div>
                )}

              {!paymentState.isStoreBlockingProgressive &&
                !paymentState.isStoreExcludedFromFinancing && (
                  <div
                    className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
                  >
                    <CustomSkeleton type="text" height="56" width="100%" />
                  </div>
                )}
              {!paymentState.isStoreBlockingAffirm &&
                !paymentState.isStoreExcludedFromFinancing && (
                  <div
                    className={`${Styles.radioSkeleton} ${Styles.synchronySkeleton}`}
                  >
                    <CustomSkeleton type="text" height="56" width="100%" />
                  </div>
                )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <PaymentContainer
      {...{
        paymentConnectors,
        cartId,
        orderDetails,
        isForterValidationEnabled,
        synchronyConfiguration,
        progressiveConfiguration,
        paymentState,
        affirmConfiguration,
        skeletonBlockedCount,
        usaEPayErrorMessages,
        featureFlags,
        isForterValidationEnabledForAdyen,
      }}
    />
  );
};
const extractPlanID = (
  selectedOffer: IFinancingOfferFormatted | undefined,
  type: CheckoutPaymentInstrument | null,
): string | undefined => {
  if (type?.paymentTenderType !== TYPES.SYNCHRONY || !selectedOffer) return;

  const syfDetails = wmapStore.getState().syfDetails;
  let everydayOfferPlanId;

  if (syfDetails?.planOfferKey) {
    const [planId] = syfDetails.planOfferKey.split(',');
    everydayOfferPlanId = planId;
  }

  if (everydayOfferPlanId) {
    return everydayOfferPlanId;
  }

  return selectedOffer?.planID || SYNCHRONY_DETAILS.DEFAULT_TENDER_TYPE;
};

function handleSyfModalHtml() {
  const initializeSynchronyButtons = (observer: MutationObserver) => {
    const btn = document.querySelector('.msax-Submit') as HTMLElement | null;
    if (btn && !btn.dataset.hooked) {
      btn.id = `${WMAP_PREFIX}-existing-account-btn`;
      const originalOnClick = btn.onclick;
      btn.onclick = (e) => {
        sendSynchronyExistingAccountUtag?.();
        originalOnClick?.call(btn, e);
      };
      btn.dataset.hooked = 'true';
    }
    const el = document.querySelector('.syfClickEvent.syfClose');
    if (el && el.id !== `${WMAP_PREFIX}-syf-close-modal-button`) {
      el.id = `${WMAP_PREFIX}-syf-close-modal-button`;
    }
    if (
      btn?.dataset.hooked &&
      el?.id === `${WMAP_PREFIX}-syf-close-modal-button`
    ) {
      observer.disconnect();
    }
  };
  const observer = new MutationObserver(() =>
    initializeSynchronyButtons(observer),
  );
  initializeSynchronyButtons(observer);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

export default WmapPayment;
